/* eslint-disable complexity */
import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import {
  ButtonBase,
  ButtonBaseProps,
  LinearProgress,
  styled,
  useTheme,
} from '@mui/material';

export interface ButtonProps extends ButtonBaseProps {
  variant?: 'default' | 'vibrant' | 'tempered' | 'paper' | 'new-paper' | 'outline';
  inverted?: boolean;
  children?: ReactNode;
  minHeight?: number | string;
  minWidth?: number | string;
  hoverEffect?: 'elevate' | 'invert' | 'underline' | 'none';
  acting?: boolean;
}

export const Button = styled(
  ({
    className,
    variant = 'default',
    inverted = false,
    children,
    minHeight,
    minWidth,
    hoverEffect = 'elevate',
    acting = false,
    ...buttonBaseProps
  }: ButtonProps): ReactElement => {
    minHeight = minHeight ?? (variant === 'tempered' ? 27 : 40);
    minWidth = minWidth ?? (variant === 'tempered' ? 'auto' : '120px');

    const theme = useTheme();

    let background: string;
    let borderColor: string;
    let color: string;

    const isInverted = inverted;
    const isVibrant = variant === 'vibrant';
    const isTempered = variant === 'tempered';
    const isPaper = variant === 'paper' || variant === 'new-paper';

    switch (variant) {
      case 'vibrant':
        background = theme.palette.vibrant.primary;
        borderColor = theme.palette.vibrant.primary;
        color = theme.palette.primary.main;
        break;
      case 'tempered':
        background = theme.palette.tempered.light;
        borderColor = theme.palette.tempered.dark;
        color = theme.palette.primary.main;
        break;
      case 'paper':
        background = '#fff';
        borderColor = '#a7a7a7';
        color = theme.palette.primary.main;
        break;
      case 'new-paper':
        background = '#fff';
        borderColor = '#23305F';
        color = '#23305F';
        break;
      case 'outline':
        background = 'transparent';
        borderColor = 'unset';
        color = '#23305F';
        break;
      default:
        background = theme.palette.primary.main;
        borderColor = theme.palette.primary.main;
        color = theme.palette.neutral.light;
    }

    if (isInverted) {
      if (isTempered) color = theme.palette.tempered.dark;
      const tmp = color;
      color = background;
      background = tmp;
      borderColor = color;

      if (variant === 'default') {
        background = theme.palette.neutral.lightest;
        borderColor = theme.palette.neutral.dark;
      }
    }

    let hoverStyles: CSSProperties;

    switch (hoverEffect) {
      case 'elevate':
        hoverStyles = {
          background,
          color,
          transform: 'scale(1.02) translate(-1px,-1px) translateZ(0)',
          boxShadow: '3px 4px 4px rgba(0,0,0,0.28)',
        };
        break;
      case 'invert':
        hoverStyles = {
          background: color,
          color: background,
        };
        break;
      case 'underline':
        hoverStyles = {
          textDecoration: 'underline',
        };
        break;
      case 'none':
      default:
        hoverStyles = {
          background,
          color,
        };
    }

    const border =
      isPaper || (isInverted && !isVibrant)
        ? '1px solid'
        : isInverted || isTempered
        ? '2px solid'
        : 'none';

    return (
      <ButtonBase
        {...buttonBaseProps}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background,
          boxShadow: 'none',
          color,
          whiteSpace: 'nowrap',
          border,
          borderColor,
          borderRadius: 6,
          textTransform: 'none',
          fontSize: '0.875rem',
          fontFamily: 'Poppins',
          minHeight,
          minWidth,
          padding: isTempered ? '5px 16px' : isPaper ? '15px 10px' : '8px 20px',
          backfaceVisibility: 'hidden',
          WebkitFontSmoothing: 'subpixel-antialiased',
          transition: '0.1875s',
          '&[disabled], &.Mui-disabled': {
            opacity: 0.37,
          },
          '&:hover': {
            textDecoration: 'none',
            ...hoverStyles,
          },
          '&:active': {
            transform: hoverEffect === 'elevate' ? 'scale(0.98)' : '',
            boxShadow: 'none',
            transition: '0s',
          },
          '&:focus-visible': {
            ...hoverStyles,
            outline: `2px auto ${theme.palette.primary.main}`,
            outlineOffset: 6,
          },
        }}
        className={className}
      >
        {children}
        {acting && (
          <LinearProgress
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
            }}
          />
        )}
      </ButtonBase>
    );
  },
)``;
